/**
 * Phase 8 — Data layer modernization.
 *
 * Each hook below is a thin TanStack Query consumer wired to:
 *  - the typed `qk` key factory in `@/lib/queryKeys` (no string-array literals)
 *  - aligned `STALE` tokens (catalog data changes rarely)
 *  - the shared Supabase client
 *
 * `prefetchManufacturer*` helpers are exposed for route-boundary warm-ups
 * (e.g. nav hover, route guards). Use:
 *   import { prefetchManufacturer } from "@/hooks/useManufacturers";
 *   prefetchManufacturer(queryClient, slug);
 */

import { useQuery, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { qk, STALE } from "@/lib/queryKeys";

export type Manufacturer = Tables<"manufacturers">;
export type ManufacturerLink = Tables<"manufacturer_links">;
export type ManufacturerCategory = Tables<"manufacturer_categories">;
export type Product = Tables<"products">;
export type Collection = Tables<"collections">;
export type CollectionImage = Tables<"collection_images">;

export type ManufacturerWithLocalizedContent = Manufacturer & {
  description_short_en?: string | null;
  description_short_it?: string | null;
  description_short_es?: string | null;
  description_short_nl?: string | null;
  description_long_en?: string | null;
  description_long_it?: string | null;
  description_long_es?: string | null;
  description_long_nl?: string | null;
  brand_positioning_en?: string | null;
  brand_positioning_it?: string | null;
  brand_positioning_es?: string | null;
  brand_positioning_nl?: string | null;
};

export type LocalizedManufacturerLink = ManufacturerLink & { manufacturer_slug: string | null };
export type LocalizedManufacturerCategory = ManufacturerCategory & { manufacturer_slug: string | null };
export type CollectionWithRelations = Collection & { collection_images: CollectionImage[] };
export type CategoryCollectionWithRelations = Collection & {
  description_en?: string | null;
  description_it?: string | null;
  description_es?: string | null;
  description_nl?: string | null;
  collection_images: CollectionImage[];
  manufacturers: { name: string; slug: string; country: string | null };
};

// ─────────────────────────────────────────────────────────────────────────────
// Fetchers — pure async functions, reusable for both useQuery and prefetching.
// ─────────────────────────────────────────────────────────────────────────────

const fetchManufacturers = async () => {
  const { data, error } = await supabase
    .from("manufacturers").select("*").order("sort_order");
  if (error) throw error;
  return data as ManufacturerWithLocalizedContent[];
};

const fetchManufacturersByType = async (type: "lamp" | "furniture") => {
  const { data, error } = await supabase
    .from("manufacturers").select("*").eq("type", type).order("sort_order");
  if (error) throw error;
  return data as ManufacturerWithLocalizedContent[];
};

const fetchManufacturer = async (slug: string) => {
  const { data, error } = await supabase
    .from("manufacturers").select("*").eq("slug", slug).maybeSingle();
  if (error) throw error;
  return data as ManufacturerWithLocalizedContent | null;
};

const fetchManufacturerLinks = async (manufacturerId: string) => {
  const { data, error } = await supabase
    .from("manufacturer_links").select("*, manufacturers(slug)")
    .eq("manufacturer_id", manufacturerId).order("sort_order");
  if (error) throw error;
  return (data ?? []).map((item) => ({
    ...(item as ManufacturerLink),
    manufacturer_slug: (item as { manufacturers?: { slug?: string | null } | null }).manufacturers?.slug ?? null,
  })) as LocalizedManufacturerLink[];
};

const fetchManufacturerCategories = async (manufacturerId: string) => {
  const { data, error } = await supabase
    .from("manufacturer_categories").select("*, manufacturers(slug)")
    .eq("manufacturer_id", manufacturerId).order("sort_order");
  if (error) throw error;
  return (data ?? []).map((item) => ({
    ...(item as ManufacturerCategory),
    manufacturer_slug: (item as { manufacturers?: { slug?: string | null } | null }).manufacturers?.slug ?? null,
  })) as LocalizedManufacturerCategory[];
};

const fetchManufacturerProducts = async (manufacturerId: string) => {
  const { data, error } = await supabase
    .from("products").select("*")
    .eq("manufacturer_id", manufacturerId).order("sort_order");
  if (error) throw error;
  return data as Product[];
};

const fetchManufacturerCollections = async (manufacturerId: string) => {
  const { data, error } = await supabase
    .from("collections").select("*, collection_images(*)")
    .eq("manufacturer_id", manufacturerId).order("sort_order");
  if (error) throw error;
  return data as CollectionWithRelations[];
};

const fetchManufacturersByCategory = async (categorySlug: string) => {
  const { data: cats, error: catErr } = await supabase
    .from("manufacturer_categories").select("manufacturer_id").eq("slug", categorySlug);
  if (catErr) throw catErr;
  const ids = cats.map((c) => c.manufacturer_id);
  if (ids.length === 0) return [] as ManufacturerWithLocalizedContent[];
  const { data, error } = await supabase
    .from("manufacturers").select("*").in("id", ids).order("sort_order");
  if (error) throw error;
  return data as ManufacturerWithLocalizedContent[];
};

const fetchProductsByCategory = async (categorySlug: string, manufacturerId?: string) => {
  let query = supabase
    .from("products").select("*").eq("category", categorySlug).order("sort_order");
  if (manufacturerId) query = query.eq("manufacturer_id", manufacturerId);
  const { data, error } = await query;
  if (error) throw error;
  return data as Product[];
};

const fetchCollectionsByCategory = async (categorySlug: string, manufacturerId?: string) => {
  let query = supabase
    .from("collections")
    .select("*, collection_images(*), manufacturers(name, slug, country)")
    .eq("category", categorySlug).order("sort_order");
  if (manufacturerId) query = query.eq("manufacturer_id", manufacturerId);
  const { data, error } = await query;
  if (error) throw error;
  return data as CategoryCollectionWithRelations[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

export const useManufacturers = () =>
  useQuery({
    queryKey: qk.manufacturers.list(),
    queryFn: fetchManufacturers,
    staleTime: STALE.short,
  });

export const useManufacturer = (slug: string) =>
  useQuery({
    queryKey: qk.manufacturers.detail(slug),
    queryFn: () => fetchManufacturer(slug),
    staleTime: STALE.medium,
    enabled: !!slug,
  });

export const useManufacturerLinks = (manufacturerId: string) =>
  useQuery({
    queryKey: qk.manufacturers.links(manufacturerId),
    queryFn: () => fetchManufacturerLinks(manufacturerId),
    staleTime: STALE.long,
    enabled: !!manufacturerId,
  });

export const useManufacturerCategories = (manufacturerId: string) =>
  useQuery({
    queryKey: qk.manufacturers.categories(manufacturerId),
    queryFn: () => fetchManufacturerCategories(manufacturerId),
    staleTime: STALE.long,
    enabled: !!manufacturerId,
  });

export const useLampManufacturers = () =>
  useQuery({
    queryKey: qk.manufacturers.byType("lamp"),
    queryFn: () => fetchManufacturersByType("lamp"),
    staleTime: STALE.short,
  });

export const useFurnitureManufacturers = () =>
  useQuery({
    queryKey: qk.manufacturers.byType("furniture"),
    queryFn: () => fetchManufacturersByType("furniture"),
    staleTime: STALE.short,
  });

export const useManufacturerProducts = (manufacturerId: string) =>
  useQuery({
    queryKey: qk.manufacturers.products(manufacturerId),
    queryFn: () => fetchManufacturerProducts(manufacturerId),
    staleTime: STALE.medium,
    enabled: !!manufacturerId,
  });

export const useManufacturerCollections = (manufacturerId: string) =>
  useQuery({
    queryKey: qk.manufacturers.collections(manufacturerId),
    queryFn: () => fetchManufacturerCollections(manufacturerId),
    staleTime: STALE.medium,
    enabled: !!manufacturerId,
  });

/** All manufacturers carrying a given category slug. */
export const useManufacturersByCategory = (categorySlug: string) =>
  useQuery({
    queryKey: qk.manufacturers.byCategory(categorySlug),
    queryFn: () => fetchManufacturersByCategory(categorySlug),
    staleTime: STALE.medium,
    enabled: !!categorySlug,
  });

/** Products in a category, optionally filtered by manufacturer. */
export const useProductsByCategory = (categorySlug: string, manufacturerId?: string) =>
  useQuery({
    queryKey: qk.products.byCategory(categorySlug, manufacturerId),
    queryFn: () => fetchProductsByCategory(categorySlug, manufacturerId),
    staleTime: STALE.medium,
    enabled: !!categorySlug,
  });

/** Collections in a category (with images + manufacturer info). */
export const useCollectionsByCategory = (categorySlug: string, manufacturerId?: string) =>
  useQuery({
    queryKey: qk.collections.byCategory(categorySlug, manufacturerId),
    queryFn: () => fetchCollectionsByCategory(categorySlug, manufacturerId),
    staleTime: STALE.medium,
    enabled: !!categorySlug,
  });

// ─────────────────────────────────────────────────────────────────────────────
// Prefetch helpers — call from route boundaries / hover intents to warm cache.
// ─────────────────────────────────────────────────────────────────────────────

export const prefetchManufacturers = (qc: QueryClient) =>
  qc.prefetchQuery({
    queryKey: qk.manufacturers.list(),
    queryFn: fetchManufacturers,
    staleTime: STALE.short,
  });

export const prefetchManufacturer = (qc: QueryClient, slug: string) =>
  Promise.all([
    qc.prefetchQuery({
      queryKey: qk.manufacturers.detail(slug),
      queryFn: () => fetchManufacturer(slug),
      staleTime: STALE.medium,
    }),
  ]);

export const prefetchCategory = (qc: QueryClient, categorySlug: string) =>
  Promise.all([
    qc.prefetchQuery({
      queryKey: qk.collections.byCategory(categorySlug, undefined),
      queryFn: () => fetchCollectionsByCategory(categorySlug),
      staleTime: STALE.medium,
    }),
    qc.prefetchQuery({
      queryKey: qk.manufacturers.byCategory(categorySlug),
      queryFn: () => fetchManufacturersByCategory(categorySlug),
      staleTime: STALE.medium,
    }),
  ]);

// ─────────────────────────────────────────────────────────────────────────────
// Category availability — derives the set of category slugs that currently
// have at least one collection in the database. Used to hide empty categories
// from global navigation (burger wheel, mega menu, products overview).
// ─────────────────────────────────────────────────────────────────────────────

const fetchAvailableCategorySlugs = async (): Promise<Set<string>> => {
  const { data, error } = await supabase.from("collections").select("category");
  if (error) throw error;
  const slugs = (data ?? [])
    .map((row: { category: string | null }) => row.category)
    .filter((s): s is string => !!s && s.trim().length > 0);
  return new Set(slugs);
};

export const useAvailableCategorySlugs = () =>
  useQuery({
    queryKey: ["categories", "available"] as const,
    queryFn: fetchAvailableCategorySlugs,
    staleTime: STALE.medium,
  });
